import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronRight, ChevronLeft, Check, Shield, FileText, Brain,
  ScrollText, Lock, Mail, User, Calendar, GraduationCap, AlertTriangle,
  X, CreditCard, Crown, Sparkles, ArrowLeft, Home, LogIn
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS_DEFAULT = [
  {
    id: 'plus',
    name: 'Plus',
    priceMonthly: 20,
    priceAnnual: 200,
    includedChildren: 1,
    credits: 400,
    icon: User,
    color: '#22d3ee',
    features: [
      'Every FuturePath module, nothing locked away',
      'Parent dashboard and progress visibility',
      'Decision Lab, funding and financial planning',
      '400 AI credits every month',
      'The full FuturePath report',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    priceMonthly: 40,
    priceAnnual: 400,
    includedChildren: 2,
    credits: 850,
    icon: User,
    color: '#a78bfa',
    popular: true,
    features: [
      'Everything in Plus, with more than twice the capacity',
      'Room to compare options properly before committing',
      'More documents, scenarios and reports each cycle',
      '850 AI credits every month',
      'Priority handling when the platform is busy',
    ],
  },
];

const STEPS = [
  { id: 'select-plan', label: 'Plan', icon: CreditCard },
  { id: 'parent-info', label: 'Account', icon: User },
  { id: 'payment', label: 'Payment', icon: CreditCard },
  { id: 'children-info', label: 'Children', icon: GraduationCap },
  { id: 'consent', label: 'Consent', icon: FileText },
  { id: 'complete', label: 'Done', icon: Check },
];

function ProgressBar({ currentStep, steps }) {
  return (
    <div className="flex items-center gap-1 mb-8">
      {steps.map((step, i) => {
        const isActive = i === currentStep;
        const isComplete = i < currentStep;
        return (
          <div key={step.id} className="flex-1 flex flex-col items-center gap-1">
            <div className={`w-full h-1.5 rounded-full transition-colors ${isComplete ? 'bg-blue-500' : isActive ? 'bg-blue-400/50' : 'bg-slate-700'}`} />
            <span className={`text-xs hidden sm:block ${isActive ? 'text-white font-medium' : isComplete ? 'text-blue-400' : 'text-slate-300'}`}>{step.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function CancelDialog({ open, onClose, navigate }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/70 backdrop-blur-md p-4 animate-in fade-in duration-200" data-testid="cancel-dialog" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl overflow-hidden bg-gradient-to-b from-slate-900/95 to-slate-950/95 border border-slate-700/60 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]"
      >
        {/* Decorative top accent bar */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-amber-500 to-blue-500" />

        <div className="p-7 space-y-6">
          {/* Brand Lockup */}
          <div className="flex items-center justify-center gap-2.5">
            <img src="/fp-logo.png" alt="Agentic FuturePath AI" className="h-9 w-9 rounded-lg shadow-[0_0_16px_rgba(59,130,246,0.35)]" />
            <span className="font-outfit font-bold text-white text-base tracking-tight">
              <span className="text-cyan-300">AGENTIC</span> FUTUREPATH <span className="text-blue-400">AI</span>
            </span>
          </div>

          <div className="text-center">
            <h3 className="font-outfit text-xl font-semibold text-white mb-1.5">Where would you like to go?</h3>
            <p className="text-sm text-slate-300">Your progress will not be saved if you leave.</p>
          </div>

          <div className="space-y-2.5">
            <button
              onClick={() => navigate('/')}
              className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-800/60 hover:bg-emerald-500/10 border border-slate-700/60 hover:border-emerald-500/40 text-slate-200 hover:text-white text-sm font-medium transition-all"
              data-testid="cancel-go-home"
            >
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/25 transition-colors">
                <Home className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="flex-1 text-left">Go to Homepage</span>
              <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all" />
            </button>

            <button
              onClick={() => navigate('/pricing')}
              className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-800/60 hover:bg-amber-500/10 border border-slate-700/60 hover:border-amber-500/40 text-slate-200 hover:text-white text-sm font-medium transition-all"
              data-testid="cancel-go-plans"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/25 transition-colors">
                <ArrowLeft className="w-4 h-4 text-amber-400" />
              </div>
              <span className="flex-1 text-left">Back to plans</span>
              <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all" />
            </button>

            <button
              onClick={() => navigate('/login')}
              className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-800/60 hover:bg-blue-500/10 border border-slate-700/60 hover:border-blue-500/40 text-slate-200 hover:text-white text-sm font-medium transition-all"
              data-testid="cancel-go-login"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/25 transition-colors">
                <LogIn className="w-4 h-4 text-blue-400" />
              </div>
              <span className="flex-1 text-left">Go to Login</span>
              <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>

          <button
            onClick={onClose}
            className="w-full py-3 rounded-full text-sm font-medium text-slate-300 hover:text-white bg-slate-800/40 hover:bg-slate-700/60 border border-slate-700/40 transition-colors"
            data-testid="cancel-stay"
          >
            Stay on this page
          </button>
        </div>
      </div>
    </div>
  );
}

function DocumentViewer({ title, sections, keyTerms, signature, setSignature, onSign, signing }) {
  const scrollRef = useRef(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const checkAtBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    // If content fits without scroll OR we're within 40px of bottom, treat as fully read
    if (el.scrollHeight - el.clientHeight < 40 || el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolledToBottom(true);
    }
  };

  // Re-evaluate when content (sections/keyTerms) changes (i.e. user advances to next doc)
  useEffect(() => {
    setScrolledToBottom(false);
    // Use rAF so DOM has rendered before measuring
    const id = requestAnimationFrame(checkAtBottom);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, keyTerms]);

  const handleScroll = () => checkAtBottom();

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <p className="text-sm text-slate-300">Please read the entire document below. You must scroll to the bottom before signing.</p>
      <div ref={scrollRef} onScroll={handleScroll}
        className="max-h-[340px] overflow-y-auto bg-[#0a1729] rounded-lg border border-slate-700 p-5 space-y-4 text-sm"
        data-testid="document-scroll-area">
        {sections?.map((sec, i) => (
          <div key={i}>
            <h4 className="font-semibold text-white mb-1">{sec.heading}</h4>
            <p className="text-slate-300 whitespace-pre-line leading-relaxed">{sec.content}</p>
          </div>
        ))}
        {keyTerms?.length > 0 && (
          <div>
            <h4 className="font-semibold text-white mb-2">Key Terms</h4>
            <ol className="list-decimal pl-5 space-y-2 text-slate-300">
              {keyTerms.map((term, i) => <li key={i}>{term}</li>)}
            </ol>
          </div>
        )}
      </div>
      {!scrolledToBottom && (
        <p className="text-amber-400 text-xs flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Scroll to the bottom to enable signing</p>
      )}
      <div className="space-y-3 pt-2">
        <label className="text-sm text-slate-300 block">Type your full legal name to sign</label>
        <Input
          value={signature} onChange={(e) => setSignature(e.target.value)}
          placeholder="Your full legal name"
          disabled={!scrolledToBottom}
          className={!scrolledToBottom ? 'opacity-50' : ''}
          data-testid="signature-input"
        />
        <Button onClick={onSign} disabled={!scrolledToBottom || !signature.trim() || signing} className="w-full" data-testid="sign-document-btn">
          {signing ? 'Signing...' : <><Check className="w-4 h-4 mr-2" /> Sign Document</>}
        </Button>
      </div>
    </div>
  );
}

export default function ParentRegistrationFlow() {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { branding } = useTenant();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenant') || '';

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCancel, setShowCancel] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'pro');
  const [billingCycle, setBillingCycle] = useState(() => {
    // Respect the Monthly/Yearly choice the user made on the Pricing page
    const fromQuery = searchParams.get('billing');
    return fromQuery === 'annual' || fromQuery === 'monthly' ? fromQuery : 'monthly';
  });
  const [PLANS, setPlans] = useState(PLANS_DEFAULT);

  useEffect(() => {
    axios.get(`${API}/plans`).then(res => {
      const live = (res.data.plans || []).filter(p => p.key !== 'trial');
      if (!live.length) return;
      // Show every paid tier so this page can never disagree with pricing.
      setPlans(live.map((p, i) => {
        const seed = PLANS_DEFAULT.find(d => d.id === p.key);
        return {
          id: p.key, name: p.name, priceMonthly: p.price_monthly, priceAnnual: p.price_annual,
          credits: p.monthly_credits, includedChildren: seed?.includedChildren ?? (i + 1),
          icon: User, color: ['#22d3ee', '#a78bfa', '#34d399', '#fbbf24'][i] || '#22d3ee',
          popular: !!p.badge,
          features: seed?.features || [
            'Every FuturePath module, nothing locked away',
            'Parent dashboard and progress visibility',
            `${p.monthly_credits?.toLocaleString()} AI credits every month`,
            p.positioning,
          ].filter(Boolean),
        };
      }));
    }).catch(() => {});
  }, []);

  // Step 1: Parent info
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 2: Children info (multi-child)
  const [numChildren, setNumChildren] = useState(1);
  const [children, setChildren] = useState([
    { firstName: '', lastName: '', dob: '', grade: '', email: '' }
  ]);

  // Step 3: Age check result
  const [childAges, setChildAges] = useState([]);
  const [ageError, setAgeError] = useState('');

  // Truth attestation
  const [truthAttested, setTruthAttested] = useState(false);

  // Auth token from parent registration
  const [authToken, setAuthToken] = useState('');

  // Per-child consent tracking
  const [currentChildForConsent, setCurrentChildForConsent] = useState(0);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const DOC_TYPES = ['parental_consent', 'ferpa_acknowledgment', 'ai_processing_disclosure', 'terms_acceptance'];

  // Documents
  const [templates, setTemplates] = useState({});
  const [signedDocs, setSignedDocs] = useState(new Set());
  const [currentSignature, setCurrentSignature] = useState('');
  const [signing, setSigning] = useState(false);

  // Step 8: Completion
  const [activationLink, setActivationLink] = useState('');
  const [studentId, setStudentId] = useState('');
  const [childCreated, setChildCreated] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`${API}/compliance/all-templates`);
        const map = {};
        for (const t of res.data.templates) {
          map[t.document_type] = t;
        }
        setTemplates(map);
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    }
    load();

    // Restore state after Stripe payment redirect
    const saved = sessionStorage.getItem('fp_parent_reg');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setAuthToken(data.token);
        setNumChildren(data.numChildren || 1);
        setSelectedPlan(data.selectedPlan || 'pro');
        setBillingCycle(data.billingCycle || 'monthly');
        setParentName(data.parentName || '');
        handleNumChildrenChange(data.numChildren || 1);
        setStep(3); // Go to children info step
        sessionStorage.removeItem('fp_parent_reg');
        toast.success('Payment successful! Now let\'s set up your children\'s accounts.');
      } catch { sessionStorage.removeItem('fp_parent_reg'); }
    }
  }, []);

  const updateChild = (index, field, value) => {
    setChildren(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const handleNumChildrenChange = (n) => {
    const num = parseInt(n);
    setNumChildren(num);
    setChildren(prev => {
      if (num > prev.length) {
        return [...prev, ...Array(num - prev.length).fill(null).map(() => ({ firstName: '', lastName: '', dob: '', grade: '', email: '' }))];
      }
      return prev.slice(0, num);
    });
  };

  const calculateAge = (dob) => {
    const d = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
  };

  // Calculate extra child cost
  const currentPlan = PLANS.find(p => p.id === selectedPlan) || PLANS[0];
  const includedChildren = currentPlan.includedChildren || (selectedPlan === 'pro' ? 2 : 1);
  const extraChildPrice = currentPlan.extraChildPrice || 11;
  const extraChildren = Math.max(0, numChildren - includedChildren);
  const extraCost = extraChildren * extraChildPrice;
  const basePriceMonthly = currentPlan.priceMonthly;
  const totalMonthly = basePriceMonthly + extraCost;
  const totalAnnual = totalMonthly * 11;
  const displayPrice = billingCycle === 'annual' ? totalAnnual : totalMonthly;
  const displayPeriod = billingCycle === 'annual' ? '/yr' : '/mo';

  const handleParentRegister = async () => {
    setError('');
    if (!parentEmail) {
      setError('Email address is required'); return;
    }
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(parentEmail.trim())) {
      setError('Please enter a valid email address (example: name@domain.com)'); return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/register/start`, {
        email: parentEmail.trim(),
        plan: selectedPlan,
        billing_period: billingCycle,
        num_children: numChildren,
        role: 'parent',
      });

      if (res.data.success && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setError('Failed to create payment session. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChildInfoNext = async () => {
    setError('');

    // Validate all children
    for (let i = 0; i < children.length; i++) {
      const c = children[i];
      if (!c.firstName || !c.lastName) {
        setError(`Child ${i + 1}: First name and last name are required`); return;
      }
      if (!c.dob) {
        setError(`Child ${i + 1}: Date of birth is required`); return;
      }
      const age = calculateAge(c.dob);
      if (age < 14) {
        setError(`${c.firstName} is ${age} years old. Agentic FuturePath AI requires students to be at least 14.`); return;
      }
      if (age >= 18) {
        setError(`${c.firstName} is ${age}. Students 18+ should register independently at /register/student.`); return;
      }
    }

    if (!truthAttested) {
      setError('Please confirm that the information you entered is accurate'); return;
    }

    // Move to consent step, we'll sign docs per child
    setCurrentChildForConsent(0);
    setCurrentDocIndex(0);
    setStep(4);
  };

  const handleSignDocument = async (docType) => {
    const child = children[currentChildForConsent];
    const childFullName = `${child?.firstName || ''} ${child?.lastName || ''}`.trim();
    setSigning(true);
    try {
      await axios.post(`${API}/compliance/sign-document`, {
        document_type: docType,
        typed_signature: currentSignature,
        child_name: childFullName,
        child_dob: child?.dob || '',
      }, { headers: { Authorization: `Bearer ${authToken}` } });

      setSignedDocs(prev => new Set([...prev, `${docType}-${currentChildForConsent}`]));
      setCurrentSignature('');
      toast.success(`${templates[docType]?.title || 'Document'} signed for ${childFullName}`);

      // Advance: next doc for same child, or first doc for next child, or done
      const nextDocIdx = currentDocIndex + 1;
      if (nextDocIdx < DOC_TYPES.length) {
        setCurrentDocIndex(nextDocIdx);
      } else {
        // All docs signed for this child, register their account
        if (child?.email) {
          try {
            await axios.post(`${API}/auth/create-child-account`, {
              child_email: child.email,
              child_first_name: child.firstName,
              child_last_name: child.lastName,
              child_dob: child.dob,
              child_grade_level: child.grade,
            }, { headers: { Authorization: `Bearer ${authToken}` } });
          } catch { /* Best effort */ }
        }

        const nextChild = currentChildForConsent + 1;
        if (nextChild < children.length) {
          setCurrentChildForConsent(nextChild);
          setCurrentDocIndex(0);
        } else {
          // All children done
          setStep(5);
        }
      }
    } catch (err) {
      showFriendlyError(err, { fallback: 'Signing failed' });
    } finally {
      setSigning(false);
    }
  };

  const handleCreateChildAccount = async () => {
    const firstChildEmail = children[0]?.email;
    if (!firstChildEmail) {
      setChildCreated(false);
      setStep(8);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/create-child-account`, {
        child_email: firstChildEmail,
      }, { headers: { Authorization: `Bearer ${authToken}` } });

      setActivationLink(res.data.activation_link);
      setStudentId(res.data.student_id);
      setChildCreated(true);

      try {
        await axios.post(`${API}/compliance/generate-consent-pdf`, {
          student_id: res.data.student_id,
          send_email: true,
        }, { headers: { Authorization: `Bearer ${authToken}` } });
      } catch { /* PDF email is best-effort */ }

      toast.success('Account created successfully!');
      setStep(8);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipInvite = () => {
    setChildCreated(false);
    setStep(8);
  };

  const brandColor = branding?.is_white_label ? branding.primary_color : '#3b82f6';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header with Cancel */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-center flex-1">
            <h1 className="text-2xl font-bold text-white">Parent Registration</h1>
            <p className="text-slate-300 text-sm mt-1">Set up a safe, authorized account for your child</p>
          </div>
          <button
            onClick={() => setShowCancel(true)}
            className="absolute right-4 top-4 sm:relative sm:right-auto sm:top-auto p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
            data-testid="cancel-registration-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <ProgressBar currentStep={step} steps={STEPS} />

        <div className="glass-card p-6 border border-slate-700" data-testid="registration-form">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2" data-testid="registration-error">
              <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}

          {/* Step 0: Plan Selection + Children Count */}
          {step === 0 && (
            <div className="space-y-5" data-testid="step-select-plan">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-white">Choose Your Plan</h3>
                <p className="text-sm text-slate-300 mt-1">Select the plan that fits your family</p>
                <p className="text-xs text-amber-400 mt-2 flex items-center justify-center gap-1"><Shield className="w-3.5 h-3.5" /> For students ages 14-17. Children under 14 are not eligible.</p>
              </div>

              {/* Billing toggle, segmented control */}
              <div className="flex items-center justify-center gap-3">
                <div className="relative inline-grid grid-cols-2 bg-slate-800 border border-slate-700 rounded-full p-0.5 select-none" role="tablist" data-testid="billing-toggle">
                  <span
                    aria-hidden="true"
                    className={`absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-2px)] rounded-full bg-blue-500 transition-transform duration-200 ease-out ${billingCycle === 'annual' ? 'translate-x-full' : 'translate-x-0'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    aria-pressed={billingCycle === 'monthly'}
                    className={`relative z-10 px-5 py-1.5 text-sm font-medium rounded-full transition-colors ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-300'}`}
                    data-testid="billing-toggle-monthly"
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('annual')}
                    aria-pressed={billingCycle === 'annual'}
                    className={`relative z-10 px-5 py-1.5 text-sm font-medium rounded-full transition-colors ${billingCycle === 'annual' ? 'text-white' : 'text-slate-300'}`}
                    data-testid="billing-toggle-annual"
                  >
                    Annual
                  </button>
                </div>
                {billingCycle === 'annual' && (
                  <span className="text-emerald-400 text-xs font-medium">1 month free</span>
                )}
              </div>

              {/* Plan cards */}
              <div className="grid gap-4">
                {PLANS.map(plan => {
                  const isSelected = selectedPlan === plan.id;
                  const Icon = plan.icon;
                  return (
                    <button
                      key={plan.id}
                      onClick={() => {
                        setSelectedPlan(plan.id);
                        if (numChildren < (plan.includedChildren || 1)) {
                          handleNumChildrenChange(plan.includedChildren || 1);
                        }
                      }}
                      className={`relative w-full text-left p-5 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500/5'
                          : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                      }`}
                      data-testid={`plan-${plan.id}`}
                    >
                      {plan.popular && (
                        <span className="absolute -top-2.5 right-4 bg-blue-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full">
                          Popular
                        </span>
                      )}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="w-5 h-5" style={{ color: plan.color }} />
                            <span className="font-semibold text-white">{plan.name}</span>
                          </div>
                          <div className="flex items-baseline gap-1 mb-3">
                            <span className="text-2xl font-bold text-white">${plan.priceMonthly}</span>
                            <span className="text-slate-300 text-sm">/mo</span>
                            <span className="text-slate-300 text-xs ml-1">({plan.includedChildren} {plan.includedChildren === 1 ? 'child' : 'children'} included)</span>
                          </div>
                          <ul className="space-y-1.5">
                            {plan.features.map((f, i) => (
                              <li key={i} className="text-sm text-slate-300 flex items-center gap-2">
                                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {f}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-600'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Number of children */}
              <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl space-y-3">
                <label className="text-sm text-white font-medium block">How many children are you registering?</label>
                <select
                  value={numChildren}
                  onChange={(e) => handleNumChildrenChange(Number(e.target.value))}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm appearance-none"
                  data-testid="num-children-select"
                >
                  <option value={1}>1 child</option>
                  <option value={2}>2 children (+${extraChildPrice}/mo)</option>
                  <option value={3}>3 children (+${extraChildPrice * 2}/mo)</option>
                  <option value={4}>4 children (+${extraChildPrice * 3}/mo)</option>
                  <option value={5}>5 children (+${extraChildPrice * 4}/mo)</option>
                  <option value={6}>6 children (+${extraChildPrice * 5}/mo)</option>
                  <option value={7}>7 children (+${extraChildPrice * 6}/mo)</option>
                  <option value={8}>8 children (+${extraChildPrice * 7}/mo)</option>
                </select>

                {/* Live pricing summary */}
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-300">{currentPlan.name} base</span>
                    <span className="text-white font-medium">${basePriceMonthly}/mo</span>
                  </div>
                  {extraChildren > 0 && (
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="text-slate-300">{extraChildren} extra {extraChildren === 1 ? 'child' : 'children'} x ${extraChildPrice}</span>
                      <span className="text-white font-medium">+${extraCost}/mo</span>
                    </div>
                  )}
                  <div className="border-t border-blue-500/20 mt-2 pt-2 flex justify-between items-center">
                    <span className="text-white font-semibold">Total</span>
                    <span className="text-blue-400 font-bold text-lg">
                      ${displayPrice}{displayPeriod}
                    </span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="text-emerald-400 text-xs mt-1 text-right">Save ${totalMonthly}/yr (1 month free)</p>
                  )}
                </div>
              </div>

              <Button onClick={() => setStep(1)} className="w-full" data-testid="plan-next-btn">
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* Step 1: Email + Pay */}
          {step === 1 && (
            <div className="space-y-4" data-testid="step-parent-info">
              <h3 className="text-lg font-semibold text-white">Enter Your Email</h3>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs">
                <Sparkles className="w-3.5 h-3.5 shrink-0" />
                <span><strong>{currentPlan.name}</strong>, {numChildren} {numChildren === 1 ? 'child' : 'children'}, <strong>${displayPrice}{displayPeriod}</strong></span>
              </div>

              <div>
                <label className="text-sm text-slate-300 block mb-1">Email Address</label>
                <Input value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} placeholder="parent@email.com" type="email" data-testid="parent-email-input" />
                <p className="text-xs text-slate-300 mt-1">You'll create your name and password after payment</p>
              </div>

              {error && <p className="text-red-400 text-sm bg-red-500/10 p-3 rounded-lg">{error}</p>}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(0)} className="flex-1"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={handleParentRegister} disabled={loading} className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600" data-testid="parent-next-btn">
                  {loading ? 'Redirecting...' : <>Pay ${displayPrice}{displayPeriod} <ChevronRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            </div>
          )}
          {/* (Old form fields removed, handled by AccountSetupPage after payment) */}

          {/* Step 2: Payment redirect (shown briefly) */}
          {step === 2 && (
            <div className="text-center py-12 space-y-4" data-testid="step-payment">
              <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto animate-pulse">
                <CreditCard className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Redirecting to Payment...</h3>
              <p className="text-slate-300 text-sm">You'll be redirected to our secure checkout.</p>
            </div>
          )}

          {/* Step 3: Children Info, Multi-child (after payment) */}
          {step === 3 && (
            <div className="space-y-4" data-testid="step-child-info">
              <h3 className="text-lg font-semibold text-white">Your Children's Information</h3>
              
              {/* Number of children dropdown */}
              <div>
                <label className="text-sm text-slate-300 block mb-1">How many children are you registering?</label>
                <select
                  value={String(numChildren)}
                  onChange={(e) => handleNumChildrenChange(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm"
                  data-testid="num-children-select"
                >
                  {[1,2,3,4,5,6,7,8].map(n => (
                    <option key={n} value={String(n)}>{n} {n === 1 ? 'child' : 'children'}{n > includedChildren ? ` (+$${(n - includedChildren) * extraChildPrice}/mo)` : ''}</option>
                  ))}
                </select>
                {extraCost > 0 && (
                  <p className="text-xs text-amber-400 mt-1">
                    Your plan includes {includedChildren} {includedChildren === 1 ? 'child' : 'children'}. {extraChildren} extra at ${extraChildPrice}/mo each = +${extraCost}/mo
                  </p>
                )}
              </div>

              {/* Child forms */}
              {children.map((child, idx) => (
                <div key={idx} className="p-4 border border-slate-700 rounded-xl space-y-3" data-testid={`child-form-${idx}`}>
                  <p className="text-sm font-medium text-slate-300">Child {idx + 1}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-300 block mb-1">First Name <span className="text-red-400">*</span></label>
                      <Input value={child.firstName} onChange={(e) => updateChild(idx, 'firstName', e.target.value)} placeholder="First name" data-testid={`child-first-name-${idx}`} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-300 block mb-1">Last Name <span className="text-red-400">*</span></label>
                      <Input value={child.lastName} onChange={(e) => updateChild(idx, 'lastName', e.target.value)} placeholder="Last name" data-testid={`child-last-name-${idx}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-300 block mb-1">Date of Birth <span className="text-red-400">*</span></label>
                      <Input type="date" value={child.dob}
                        min={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 17); return d.toISOString().slice(0,10); })()}
                        max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 14); return d.toISOString().slice(0,10); })()}
                        onChange={(e) => updateChild(idx, 'dob', e.target.value)} data-testid={`child-dob-${idx}`} />
                      {child.dob && /^\d{4}-\d{2}-\d{2}$/.test(child.dob) && (
                        <p className={`text-xs mt-1 ${calculateAge(child.dob) >= 14 && calculateAge(child.dob) < 18 ? 'text-emerald-400' : 'text-red-400'}`}>
                          Age: {calculateAge(child.dob)}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-300 block mb-1">Grade Level</label>
                      <select
                        value={child.grade}
                        onChange={(e) => updateChild(idx, 'grade', e.target.value)}
                        className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        <option value="">Select grade</option>
                        <option value="9th">9th Grade</option>
                        <option value="10th">10th Grade</option>
                        <option value="11th">11th Grade</option>
                        <option value="12th">12th Grade</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-300 block mb-1">Email <span className="text-slate-300">(optional, can invite later)</span></label>
                    <Input type="email" value={child.email} onChange={(e) => updateChild(idx, 'email', e.target.value)} placeholder="child@email.com" data-testid={`child-email-${idx}`} />
                  </div>
                </div>
              ))}

              {/* Truth attestation */}
              <label className="flex items-start gap-3 p-3 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer" data-testid="truth-attestation">
                <input
                  type="checkbox"
                  checked={truthAttested}
                  onChange={(e) => setTruthAttested(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-600 accent-blue-500"
                />
                <span className="text-sm text-slate-300">
                  I confirm that the information I have entered for my {numChildren === 1 ? 'child' : 'children'} is true and accurate. I understand that providing false information may result in account termination.
                </span>
              </label>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(0)} className="flex-1"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={handleChildInfoNext} disabled={loading || !truthAttested} className="flex-1" data-testid="child-next-btn">
                  {loading ? 'Verifying...' : <>Continue to Consent <ChevronRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Per-child Consent Documents */}
          {step === 4 && (() => {
            const child = children[currentChildForConsent];
            const childFullName = `${child?.firstName || ''} ${child?.lastName || ''}`.trim();
            const docType = DOC_TYPES[currentDocIndex];
            const template = templates[docType];

            if (!template) return (
              <div className="text-center py-8 space-y-3">
                <p className="text-slate-300 text-sm">Loading consent documents...</p>
                <button
                  onClick={() => {
                    axios.get(`${API}/compliance/all-templates`).then(res => {
                      const map = {};
                      for (const t of res.data.templates) map[t.document_type] = t;
                      setTemplates(map);
                    }).catch(() => toast.error('Failed to load. Please try again.'));
                  }}
                  className="text-blue-400 hover:text-blue-300 text-sm underline"
                >
                  Click here to retry
                </button>
              </div>
            );

            // Build sections with child name embedded
            const enhancedSections = template.sections?.map(sec => ({
              ...sec,
              content: sec.content
                .replace(/the student identified during registration[^.]*\.?/gi, `${childFullName}, born ${child?.dob || 'N/A'}, whose parent/guardian ${parentName} provides consent.`)
                .replace(/your child/gi, childFullName)
                .replace(/the child named above/gi, childFullName)
                .replace(/the minor/gi, childFullName)
            }));

            return (
              <div data-testid={`step-consent-${currentChildForConsent}-${currentDocIndex}`}>
                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <GraduationCap className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-300">Signing for: <strong className="text-white">{childFullName}</strong></span>
                  </div>
                  <span className="text-xs text-slate-300">
                    Child {currentChildForConsent + 1}/{children.length} | Doc {currentDocIndex + 1}/{DOC_TYPES.length}
                  </span>
                </div>
                <DocumentViewer
                  title={`${template.title}, ${childFullName}`}
                  sections={enhancedSections}
                  keyTerms={template.key_terms}
                  signature={currentSignature}
                  setSignature={setCurrentSignature}
                  onSign={() => handleSignDocument(docType)}
                  signing={signing || loading}
                />
              </div>
            );
          })()}

          {/* Step 5: Completion */}
          {step === 5 && (
            <div className="space-y-6 text-center" data-testid="step-complete">
              <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                <Check className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white">You're All Set!</h3>

              {/* Plan summary */}
              <div className="bg-slate-800/50 rounded-lg p-4 text-left">
                <p className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-400" /> Your Plan
                </p>
                <p className="text-sm text-slate-300">
                  <strong className="text-white">{selectedPlan === 'free' ? 'Free' : currentPlan.name}</strong>
                  {selectedPlan !== 'free' && ` - $${displayPrice}/mo (${billingCycle})`}
                </p>
              </div>

              {childCreated ? (
                <div className="bg-slate-800/50 rounded-lg p-4 text-left space-y-2">
                  <p className="text-sm font-medium text-white mb-2">Children Registered:</p>
                  {children.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 bg-slate-700/30 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 text-xs font-bold">{i + 1}</div>
                      <div>
                        <p className="text-sm text-white font-medium">{c.firstName} {c.lastName}</p>
                        <p className="text-xs text-slate-300">{c.dob ? `Age ${calculateAge(c.dob)}` : ''}{c.grade ? `, ${c.grade} grade` : ''}{c.email ? `, ${c.email}` : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-800/50 rounded-lg p-4 text-left">
                  <p className="text-sm text-slate-300">
                    Authorization documents signed for {children.map(c => `${c.firstName} ${c.lastName}`).join(', ')}.
                    You can invite them to activate their accounts from your parent dashboard.
                  </p>
                </div>
              )}

              <div className="bg-slate-800/50 rounded-lg p-4 space-y-2 text-left">
                <p className="text-sm font-medium text-white">What happens next:</p>
                <ul className="text-sm text-slate-300 space-y-1.5">
                  {childCreated && children.some(c => c.email) && (
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" /> Your child will receive an activation email</li>
                  )}
                  {!childCreated && (
                    <li className="flex items-start gap-2"><Mail className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" /> Invite your child from the parent dashboard when you're ready</li>
                  )}
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" /> Track their progress from your parent dashboard</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" /> A PDF copy of all signed documents has been emailed to you</li>
                </ul>
              </div>

              {activationLink && (
                <div className="bg-slate-800/50 rounded-lg p-4 text-left">
                  <p className="text-sm text-slate-300 mb-2">Activation link (also sent via email):</p>
                  <code className="text-xs text-blue-400 break-all block bg-slate-900 p-2 rounded">{activationLink}</code>
                </div>
              )}

              <Button onClick={() => {
                if (authToken) {
                  localStorage.setItem('futurepath_token', authToken);
                  window.location.href = '/app/family';
                } else {
                  navigate('/login');
                }
              }} className="w-full" data-testid="go-to-dashboard-btn">
                Go to My Parent Dashboard <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-slate-300 text-xs mt-6">
          Learn about our commitment to safety and privacy at the <button onClick={() => navigate('/trust')} className="text-blue-400 hover:underline">Trust Center</button>
        </p>
      </div>

      <CancelDialog open={showCancel} onClose={() => setShowCancel(false)} navigate={navigate} />
    </div>
  );
}
